import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SpeakerHigh, SpeakerX, PawPrint, ArrowRight } from "@phosphor-icons/react";
import { useMusic } from "@/context/MusicContext";

const IMG = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/";

// 🎬 REAL VIDEO SLOT — paste your mastered .mp4 (the full 8–10 min car-ride film) here and it plays
// on the drive-in screen instead of the AI stills reel below. Leave "" to use the reel.
const HEAVEN_VIDEO_URL = "";

// Soundtrack is handled globally by MusicContext (Imagine → Rise Up hand-off) so it keeps
// playing site-wide. The drive-in play button just controls that same global player.

// The story arc — original captions of struggle → loss → the sanctuary we're building.
// Ends as a teaser to shop + donate toward the $3M "Heaven" goal. (No song lyrics.)
const SCENES = [
  { img: "de7084fed1059ba5ae5d4a2f938f538d865c082169863d8fbf34c34af8b97d0f", kicker: "Where it started", line: "Chained, forgotten, thrown away. That's where we found them." },
  { img: "5f12b3ff24900e2f13b478aec8c971a81c5cda73b1febd6e2f67473af20c2f70", kicker: "The promise", line: "I made them one promise: no more cages. Only home." },
  { img: "487377ec66b01e1e81c57d55bd93ba692bdcc7111150001ce0a85d58c18d2f84", kicker: "The pack", line: "Smiley. Beats. King. They taught me loyalty the hard way." },
  { img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/82qnwmxq_image-2.jpeg", kicker: "Gone too soon", line: "I lost all three. But love like that never really leaves." },
  { img: "8bd8d0b15dac2c10444600512ee362203017eb8d129a0cfc9c1444be61f75e46", kicker: "Watching over", line: "Now they watch from Heaven — so I'm building Heaven back down here." },
  { img: "7cd2f7431c3662744b46c07fc37fe2ad179a80c03bbb0f995f77cf7a4a595314", kicker: "The pull-up", line: "So every ride now ends the same way — pointed home." },
  { img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wiggles-unified/artifacts/2pfs8ejt_image-7%20%282%29.jpeg", kicker: "Through the gates", line: "ALL DOGS GO TO HEAVEN — 300 acres, so no pit ever dies alone." },
  { img: "a5c272303f9221894a6f556082185834f66aea6fb47a24fa0d5bddec6b12322a", kicker: "The long road", line: "A last mile of dirt — the only road that leads somewhere good." },
  { img: "b4113042fc4d705ba45d0457f4b54383442864e62b2da3591f66d08a40ff5ffc", kicker: "The moment", line: "And that's when it hits him — he's safe. He's finally home." },
  { img: "8cfdb265e2d74617a48417cfa01068f885e4fff86c3af43e27d2ac1ad6f345e0", kicker: "Come home", line: "A pond, cabins, a treehouse — for the dogs and the kids who need them." },
  { img: "f628fddf55bc9101a13785597d4013aa5097c4c1142e69e73b4d7cdaa15d48ae", kicker: "Save the children", line: "Rescue a dog, and you just might rescue a kid too." },
  { img: "e73d06e5de8660c0d022c4a01ef0e69912d768df84eeafc464f1787cc343aa45", kicker: "Wear the mission", line: "Every hoodie you wear funds a fence, a meal, a rescue." },
  { img: "988df4e026456280f42fd2e7f047448cb7712d35bd1de6467fbd3c26b9745c85", kicker: "Twin up", line: "Match your pit. Rep the pack. It all goes to Heaven." },
  { img: "217753e7b56077f3a23412a0640ec75523db8ad238f3da7aa8171d8e3e1e406d", kicker: "For them", line: "Every hoodie, every dollar brings one more home. Wear one. Save one." },
];

const SCENE_MS = 5200;

// Simple seated dog silhouette (head + ears + shoulders) for the drive-in audience.
const DogHead = ({ style }) => (
  <svg viewBox="0 0 100 110" style={style} fill="currentColor" aria-hidden>
    <path d="M22 42 L14 6 L40 32 Z" />
    <path d="M78 42 L86 6 L60 32 Z" />
    <ellipse cx="50" cy="52" rx="30" ry="30" />
    <path d="M8 110 Q50 58 92 110 Z" />
  </svg>
);
const AUDIENCE = [46, 60, 52, 74, 58, 82, 54, 68, 48, 64, 50];

export default function DriveInTheater() {
  const { playing, muted, toggle, toggleMute } = useMusic();
  const [i, setI] = useState(0);
  const timer = useRef(null);

  const schedule = () => {
    clearInterval(timer.current);
    timer.current = setInterval(() => setI((p) => (p + 1) % SCENES.length), SCENE_MS);
  };
  useEffect(() => { schedule(); return () => clearInterval(timer.current); }, []);
  const go = (n) => { setI(n); schedule(); };

  const scene = SCENES[i];

  return (
    <section className="relative overflow-hidden bg-black py-20 sm:py-28" data-testid="drive-in-theater">
      {/* night sky */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 50% 0%, #14061f 0%, #05040a 55%, #000 100%)" }} />
      <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(1px 1px at 20% 15%, #fff, transparent), radial-gradient(1px 1px at 70% 25%, #fff, transparent), radial-gradient(1px 1px at 40% 8%, #9df, transparent), radial-gradient(1px 1px at 85% 12%, #fff, transparent), radial-gradient(1px 1px at 55% 30%, #f7a, transparent)", backgroundRepeat: "no-repeat" }} />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        {/* marquee */}
        <div className="text-center mb-6">
          <span className="overline text-secondary inline-flex items-center gap-2"><PawPrint weight="fill" size={16} /> The Wigglez Drive-In</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-none">
            NOW SHOWING · <span className="neon-text">HEAVEN</span>
          </h2>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base max-w-xl mx-auto">A short film for Smiley, Beats & King — and every pit still waiting. Pull up. The pack saved you a seat.</p>
        </div>

        {/* screen on posts */}
        <div className="relative mx-auto" style={{ maxWidth: 880 }}>
          {/* posts */}
          <div className="absolute left-[12%] top-8 bottom-[-40px] w-2 bg-gradient-to-b from-white/15 to-transparent rounded" />
          <div className="absolute right-[12%] top-8 bottom-[-40px] w-2 bg-gradient-to-b from-white/15 to-transparent rounded" />

          <div className="relative rounded-lg border-[6px] border-neutral-800 bg-neutral-900 shadow-2xl overflow-hidden neon-glow" data-testid="drive-in-screen">
            <div className="relative aspect-video bg-black overflow-hidden">
              {HEAVEN_VIDEO_URL ? (
                <video src={HEAVEN_VIDEO_URL} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" data-testid="drive-in-video" />
              ) : (
                <>
                  <AnimatePresence mode="popLayout">
                    <motion.img
                      key={i}
                      src={scene.img.startsWith("http") ? scene.img : IMG + scene.img + ".jpeg"}
                      alt={scene.line}
                      initial={{ opacity: 0, scale: 1.16 }}
                      animate={{ opacity: 1, scale: 1.02 }}
                      exit={{ opacity: 0 }}
                      transition={{ opacity: { duration: 1 }, scale: { duration: SCENE_MS / 1000, ease: "linear" } }}
                      className="absolute inset-0 h-full w-full object-cover"
                      data-testid="drive-in-frame"
                    />
                  </AnimatePresence>
                  {/* cinematic letterbox + grain + caption */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30 pointer-events-none" />
                  <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
                  <div className="absolute inset-x-0 top-0 h-[8%] bg-black" />
                  <div className="absolute inset-x-0 bottom-0 h-[8%] bg-black" />
                  <div className="absolute left-0 right-0 bottom-[12%] px-6 sm:px-10 text-left">
                    <AnimatePresence initial={false}>
                      <motion.div key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }}>
                        <span className="overline text-secondary">{scene.kicker}</span>
                        <p className="font-display text-xl sm:text-3xl text-white leading-tight mt-1 max-w-xl drop-shadow-lg" data-testid="drive-in-caption">{scene.line}</p>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </>
              )}

              {/* play overlay */}
              {!playing && (
                <button onClick={toggle} className="absolute inset-0 grid place-items-center bg-black/30 hover:bg-black/20 transition-colors z-10" data-testid="drive-in-play">
                  <span className="h-20 w-20 rounded-full bg-primary/90 grid place-items-center neon-glow animate-pulse">
                    <Play weight="fill" size={34} className="text-primary-foreground ml-1" />
                  </span>
                </button>
              )}

              {/* controls */}
              <div className="absolute top-3 right-3 z-20 flex gap-2">
                <button onClick={toggle} className="h-9 w-9 grid place-items-center rounded-full bg-black/60 backdrop-blur border border-white/15 text-white hover:text-primary" data-testid="drive-in-toggle">
                  {playing ? <Pause weight="fill" size={16} /> : <Play weight="fill" size={16} />}
                </button>
                <button onClick={toggleMute} className="h-9 w-9 grid place-items-center rounded-full bg-black/60 backdrop-blur border border-white/15 text-white hover:text-primary" data-testid="drive-in-mute">
                  {muted ? <SpeakerX size={16} /> : <SpeakerHigh size={16} />}
                </button>
              </div>
            </div>
          </div>

          {/* progress dots */}
          <div className="flex justify-center gap-1.5 mt-5">
            {SCENES.map((_, n) => (
              <button key={n} onClick={() => go(n)} aria-label={`Scene ${n + 1}`}
                className={`h-1.5 rounded-full transition-all ${n === i ? "w-6 bg-primary" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
                data-testid={`drive-in-dot-${n}`} />
            ))}
          </div>
        </div>

        {/* audience of pit bulls watching the movie */}
        <div className="relative mt-4 h-20 sm:h-28">
          <div className="absolute inset-x-0 bottom-4 mx-auto max-w-3xl h-16 rounded-[50%] opacity-60 blur-2xl" style={{ background: "radial-gradient(60% 100% at 50% 100%, rgba(255,20,147,0.45), rgba(0,229,229,0.22), transparent)" }} />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-end justify-center gap-2 sm:gap-4" data-testid="drive-in-audience">
            {AUDIENCE.map((w, idx) => (
              <DogHead key={idx} style={{ width: w, height: w * 1.05, color: "#08060c", filter: "drop-shadow(0 -1px 5px rgba(176,38,255,0.55))" }} />
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="relative text-center -mt-2">
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6 text-sm sm:text-base">
            Every hoodie funds the fences, the food and the freedom. Help us reach <span className="text-primary font-medium">$3M</span> and open the gates of Heaven — for the pack, and for the kids who need them.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="drive-in-shop-cta">
              <PawPrint weight="fill" size={18} /> Shop the Movement
            </Link>
            <Link to="/rescue" className="inline-flex items-center gap-2 border border-secondary/60 text-secondary px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-secondary/10 transition-colors neon-glow-aqua" data-testid="drive-in-mission-cta">
              Adopt · Donate · Build Heaven <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

    </section>
  );
}
